import { RouterRootState } from "connected-react-router";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Description from "../../../components/description/Description";
import { useDialog } from "../../../components/dialog/DialogContext";
import ItemContentTabTwo from "../../../components/item-content-two/item-content-tab_two";
import { Topic } from "../../../components/item-content-two/Topic";
import SimpleItemDetails from "../../../components/item-content/SimpleItemDetails";
import { SecurityPolicyCertificates } from "../../../components/security-policies/SecurityPolicyCerticatesTabs";
import { SecurityPolicyDetails } from "../../../components/security-policies/SecurityPolicyDetails";
import { SecurityPolicyPassword } from "../../../components/security-policies/SecurityPolicyPassword";
import { toast } from "react-toastify";
import { SECURITY_POLICY_ID_FIELDS, SECURITY_POLICY_SUB_TYPES } from "../../../constants/security-policy-constants";
import { useLocale } from "../../../contexts/localeContext";
import { TopicContextProvider } from "../../../contexts/topic/TopicContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import UserLoginColumns from "../../../models/ColumnDefinitions/UserLoginColumns";
import { ContentWrapper } from "../../../models/ContentWrapper";
import { ListData } from "../../../models/ListData";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { Name } from "../../../models/Name";
import SiteSearch from "../../../models/search/SiteSearch";
import searchColumns from "../../../models/search/UserLoginSearch";
import { SecurityPolicies } from "../../../models/SecurityPolicy";
import { SettingData } from "../../../models/SettingData";
import { TabSummary } from "../../../models/TabSummary";
import { getUserLogins, getUserLoginsBySecurityPolicyId } from "../../../services/loginService";
import { getSecurityPoliciesWithPagination, getSecurityPolicyById, saveSecurityPolicyService, securityPolicyUserLogin, storeSecurityPolicy } from "../../../services/securityPolicyService";
import { saveSettingData } from "../../../services/settingDataService";
import { getSiteNames, getSiteNamesWithPagination } from "../../../services/siteService";
import {getErrorMessage, showError, showLoadItemError} from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { appendFilter } from "../../../utilities/filters";
import AssignedUserLogins from "../../../components/security-policies/assignedUserLogins";
import { useGlobalState } from "../../../Providers";
import { SPECIFIC_HELP_KEY } from "../../../constants/specific-help-constant";
import { SecurityPolicyKeysTab } from "@/components/security-policies/SecurityPolicyKeysTab";
import AssignModalTwo from "@/components/assignWizard/AssignModal.tsx";
import {PaginationModel} from "@/models/Pagination.ts";

const getIdFromPath = (path: string) => {
    const splitPath = path.split("/");
    return splitPath[splitPath.length - 1];
};

const initialItem: SecurityPolicies = {
    name: "",
    profileType: "",
    profileTypeId: 0,
    profileSubType: "User Security",
    profileSubTypeId: SECURITY_POLICY_SUB_TYPES.PASSWORD_SUB_TYPE_ID,
    displayName: "",
    site: "",
    siteId: 0,
    isDefault: false,
    id: 0,
    enforceSpecialCharacters: false,
    enforceUppercase: false,
    enforceLowercase: false,
    enforceDigits: true,
    enforceInitialPasswordChange: false,
    enforcePasswordLifetime: false,
    passwordLifetime: 3,
    enforcePasswordMinLength: true,
    passwordMinLength: 6,
    enforcePasswordHistory: false,
    passwordHistoryNumber: 1,
};

const allItemsPath = "/access-control/configuration/security-policies";
const itemPath = "/access-control/configuration/security-policy/";

const SecurityPolicy = () => {
    const { translate } = useLocale();

    const passwordInitialTabs = [
        {
            label: translate.byKey(TranslationKeys.password),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.members),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.notes),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
    ];

    const certificatesInitialTabs = [
        {
            label: translate.byKey(TranslationKeys.certificates),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.keys),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.notes),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
    ];

    const dialog = useDialog();
    const [oldSelectedIds, setOldSelectedIds] = useState<string[]>([]);
    const [responseSubType, setResponseSubType] = useState<number>(0);
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [currentItem, setCurrentItem] = useState<SecurityPolicies>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>([]);
    const [site, setSite] = useState<Name[]>([]);
    const [itemId, setItemId] = useState(pageId);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [showManagedModal, setShowManagedModal] = useState(false);
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectedDisabledIds, setSelectedDisabledIds] = useState<string[]>([]);
    let settingDataToEdit: SettingData[] = [];
    const { setSpecificHelpTopicKey } = useGlobalState() ?? { setSpecificHelpTopicKey: undefined };

    useEffect(() => {
        buildSiteData();
    }, []);

    useEffect(() => {
        setItemId(pageId);
    }, [pageId]);

    const buildSiteData = async () => {
        try {
            const site = await getSiteNames();
            setSite(site);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    };

    useEffect(() => {
        reloadPage();
        dataRefresh((listParameters?: ListQuery) => getUserLoginsBySecurityPolicyId({ ...listParameters,  filters: appendFilter(itemId, "SecurityPolicy", "securityPolicy.id", []) }));
        setLookup([{
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }]);
    }, [itemId]);

    const updateTabs = (subType: number, memberCount: number) => {
        const initialTabs = subType == SECURITY_POLICY_SUB_TYPES.PASSWORD_SUB_TYPE_ID ? passwordInitialTabs : certificatesInitialTabs;
        if (subType == SECURITY_POLICY_SUB_TYPES.PASSWORD_SUB_TYPE_ID) {
            setTabs([
                initialTabs[0],
                { ...initialTabs[1], badge: memberCount },
                initialTabs[2]
            ]);
        } else if (subType == SECURITY_POLICY_SUB_TYPES.CERT_SUB_TYPE_ID) {
            setTabs([
                { ...initialTabs[0], badge: memberCount },
                initialTabs[1]
            ]);
        }
    };

    const buildData = useCallback(async (subType: number, listParameters?: ListQuery) => {
        const data = await getUserLoginsBySecurityPolicyId({ ...listParameters, filters: appendFilter(itemId, "SecurityPolicy", "securityPolicy.id", []) });
        const loginIds = data.rows.map((loginId) => loginId.id.toString());
        const pageInfo = data.pagination.totalCount;
        setSelectedIds(loginIds);
        setSelectedDisabledIds(loginIds);
        setOldSelectedIds(loginIds);
        if (itemId) {
            updateTabs(subType, pageInfo);
        }
    }, [itemId, user.securityPolicyId]);

    const reloadPage = async () => {
        try {
            if (itemId) {
                const securityPolicy = await getSecurityPolicyById(itemId, translate);
                const subType = securityPolicy.profileSubTypeId;
                setResponseSubType(subType);
                if (Number(securityPolicy.profileSubTypeId) === SECURITY_POLICY_SUB_TYPES.PASSWORD_SUB_TYPE_ID) {
                    setTabs(passwordInitialTabs);
                    setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.SOFTWARE_SECURITY_POLICY);
                } else if (Number(securityPolicy.profileSubTypeId) === SECURITY_POLICY_SUB_TYPES.CERT_SUB_TYPE_ID) {
                    setTabs(certificatesInitialTabs);
                    setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.CERTIFICATE_SECURITY_POLICY);
                }
                setCurrentItem(securityPolicy);
                setSelectedItem({ label: securityPolicy.name, value: securityPolicy.id.toString() });
                setBreadcrumb([
                    {
                        value: "/access-control/configuration",
                        displayName: translate.byKey(TranslationKeys.configuration),
                        disableClick: true,
                    },
                    {
                        value: allItemsPath,
                        displayName: translate.byKey(TranslationKeys.securityPolicies),
                    },
                    {
                        value: itemPath + `${itemId}`,
                        displayName: `${securityPolicy?.name ?? ""}`,
                    },
                ]);
                await buildData(subType);
            } else {
                setCurrentItem(initialItem);
                setTabs([passwordInitialTabs[0], passwordInitialTabs[2]]); // don't show members tab
                setBreadcrumb([
                    {
                        value: "/access-control/configuration",
                        displayName: translate.byKey(TranslationKeys.configuration),
                        disableClick: true,
                    },
                    {
                        value: allItemsPath,
                        displayName: translate.byKey(TranslationKeys.securityPolicies),
                    },
                ]);
            }
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath); });
        }
    };

    const topic: Topic = {
        accessor: "name",
        title: translate.byKey(TranslationKeys.securityPolicies),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    };

    const certificateTabContentList: ContentWrapper[] = [
        {
            content: <SecurityPolicyCertificates />,
        },
        {
            content: <SecurityPolicyKeysTab />,
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} accessor="description" />,
        },
    ];

    const passwordTabContentList: ContentWrapper[] = [
        {
            content: <SecurityPolicyPassword />,
        },
        {
            content: <AssignedUserLogins
                itemId={itemId}
                user={user}
                translate={translate}
                lookup={lookup}
                dataMethod={(listParameters) => getUserLoginsBySecurityPolicyId({ ...listParameters, filters: appendFilter(itemId, "SecurityPolicy", "securityPolicy.id", listParameters?.filters ?? []) })}
                setShowManagedModal={setShowManagedModal}
                setDataRefresh={setDataRefresh} />,
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} accessor="description" />,
        },
    ];

    const getTabContent = (index?: number) => {
        if (index === undefined || index >= tabs.length) {
            return null;
        }

        const currentTabLabel = tabs[index].label;
        const currentTabIndex = tabs.findIndex((tab) => tab.label === currentTabLabel);
        // if responseSubType == 0, it means we are creating a new policy which defaults to a User Security policy
        if (responseSubType == SECURITY_POLICY_SUB_TYPES.PASSWORD_SUB_TYPE_ID || responseSubType == 0) {
            return passwordTabContentList[currentTabIndex].content;
        }
        else if (responseSubType == SECURITY_POLICY_SUB_TYPES.CERT_SUB_TYPE_ID) {
            if (index === 2) {
                return certificateTabContentList[currentTabIndex-1].content;
            } else {
                return certificateTabContentList[currentTabIndex].content;
            }
        }
      };

    const findChangedAttributes = (updatedValues, initialValues): string[] => {
        const changedAttributes: string[] = [];
        for (const key in updatedValues) {
            if (updatedValues.hasOwnProperty(key) && initialValues.hasOwnProperty(key)) {
                if (updatedValues[key] !== initialValues[key]) {
                    changedAttributes.push(key);
                }
            }
        }
        return changedAttributes;
    };

    const buildSettingData = (accessor: string, elementSysname: string, values) => {
        const settingData = values.settingDatasRef.find((settingDataRef) => settingDataRef.elementSysname === elementSysname);
        let sdValstring = values[accessor];
        // check if value is a boolean and convert to "1" or "0"
        if(typeof sdValstring === 'boolean'){
            sdValstring = sdValstring ? "1" : "0"
        }
        settingDataToEdit.push({
            id: settingData.id,
            sdValstring: sdValstring,
        });
    };

    const updateSecurityPolicy = (values) => {
        setBusy(true);
        settingDataToEdit = [];

        const fields: { accessor: string; elementSysname: string }[] = [
            {
                accessor: "enforceUppercase",
                elementSysname: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_UPPERCASE,
            },
            {
                accessor: "enforceDigits",
                elementSysname: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_DIGITS,
            },
            {
                accessor: "enforcePasswordLifetime",
                elementSysname: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_PWD_LIFETIME,
            },
            {
                accessor: "enforcePasswordMinLength",
                elementSysname: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_MIN_LENGTH,
            },
            {
                accessor: "enforcePasswordHistory",
                elementSysname: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_PWD_HISTORY,
            },
            {
                accessor: "enforceLowercase",
                elementSysname: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_LOWERCASE,
            },
            {
                accessor: "enforceSpecialCharacters",
                elementSysname: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_SPECIAL_CHAR,
            },
            {
                accessor: "enforceInitialPasswordChange",
                elementSysname: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_INITIAL_PWD_CHANGE,
            },
            {
                accessor: "passwordLifetime",
                elementSysname: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_PWD_LIFETIME_MONTHS,
            },
            {
                accessor: "passwordMinLength",
                elementSysname: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_MIN_LENGTH,
            },
            {
                accessor: "passwordHistoryNumber",
                elementSysname: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_PWD_HISTORY_TOTAL,
            },
        ];

        const changedValues: string[] = findChangedAttributes(values, currentItem);
        const nameOrDescriptionChanged = changedValues.some(attr => attr === 'name' || attr === 'description');

        // Separate promises for settingData and name/description updates
        const settingDataPromises: Promise<any>[] = [];
        let nameDescriptionPromise: Promise<any> | null = null;

        changedValues.forEach((accessor) => {
            const field = fields.find((field) => field.accessor === accessor);
            if (field) {
                buildSettingData(field.accessor, field.elementSysname, values);
            }
        });

        if (settingDataToEdit.length > 0) {
            settingDataPromises.push(...settingDataToEdit.map(settingData =>
                saveSettingData(settingData)
            ));
        }

        if (nameOrDescriptionChanged) {
            nameDescriptionPromise = storeSecurityPolicy({
                id: values.id,
                name: values.name,
                description: values.description,
                site: site[0]?.value,
                subType: responseSubType
            },false);
        }

        const allPromises = [...settingDataPromises];
        if (nameDescriptionPromise) {
            allPromises.push(nameDescriptionPromise);
        }

        Promise.all(allPromises)
            .then((results) => {
                const allSuccessful = results.every((result) => result.success);
                if (allSuccessful) {
                    toast.success(translate.byKey(TranslationKeys.securityPolicyUpdated));
                    setCurrentItem({ ...currentItem, ...values });
                } else {
                    throw new Error(translate.byKey(TranslationKeys.error));
                }
            })
            .catch((error) => {
                dialog.pushError({
                    title: translate.byKey(TranslationKeys.error),
                    message: getErrorMessage(error),
                });
            })
            .finally(() => {
                setBusy(false);
            });
    };


    const saveSecurityPolicy = (values) => {
        if (values.id) {
            updateSecurityPolicy(values);
        } else {
            createPolicy(values);
        }
    };

    const createPolicy = (values:any) => {
        setBusy(true);
        saveSecurityPolicyService({
            name: values.name, description: values.description,
            siteId:siteId, enforceDigits:values.enforceDigits,
            enforceUppercase: values.enforceUppercase,
            enforcePasswordLifetime: values.enforcePasswordLifetime,
            enforcePasswordMinLength: values.enforcePasswordMinLength,
            enforcePasswordHistory: values.enforcePasswordHistory,
            enforceLowercase: values.enforceLowercase,
            enforceSpecialCharacters: values.enforceSpecialCharacters,
            enforceInitialPasswordChange: values.enforceInitialPasswordChange,
            passwordLifetime: values.passwordLifetime,
            passwordMinLength: values.passwordMinLength,
            passwordHistoryNumber: values.passwordHistoryNumber
         }).then((value) => {
            if(value.success) {
                toast.success(translate.byKey(TranslationKeys.policySaved));
                history.push(allItemsPath);
            }
            else {
                toast.error(translate.byKey(TranslationKeys.savingError));
            }
         }).catch((error) => {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(error)
            });
         }).finally(() => {
            setBusy(false);
        });
    };

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>, setAlwaysSelectedAndDisabled?: React.Dispatch<React.SetStateAction<string[]>>) => {
        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = [];
            do {
                let response = await getUserLoginsBySecurityPolicyId({ filters: itemId ? appendFilter(itemId, "SecurityPolicy", "securityPolicy.id", []) : [], pageSize: 100, goToPage: pageNumber });
                ids = ids.concat(response.rows.map(row => row.id.toString()))
                rows = rows.concat(response.rows);
                pagination = response.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages);

            setSelectedIds(ids);
            setSelectedDisabledIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
            setAlwaysSelectedAndDisabled && setAlwaysSelectedAndDisabled(ids);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusyLoader(false);
        }
    }

    const assignSecurityPolicy = (toBeAssigned: string[], toBeUnassigned: string[]) => {
        setBusy(true);

        securityPolicyUserLogin(itemId, toBeAssigned)
            .then(() => {
                let newTab = [...tabs];
                newTab[1].badge = toBeAssigned.length;
                setTabs(newTab);
                toast.success(translate.byKey(TranslationKeys.securityPolicyUpdated));
                reloadPage();
            }).catch(error => {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(error),
            });
        }).finally(() => {
            setBusy(false);
        });
    };

    return (
        <>
            <TopicContextProvider
                dataMethod={getSecurityPoliciesWithPagination}
                topic={topic}
                itemId={itemId}
                selectedItem={selectedItem}>
                <ItemContentTabTwo>
                    <SimpleItemDetails
                        breadcrumb={breadcrumb}
                        busy={busy}
                        getTabContent={getTabContent}
                        itemProfile={currentItem}
                        onBack={() => history.push(allItemsPath)}
                        saveDetails={saveSecurityPolicy}
                        tabs={tabs}
                        initialValues={currentItem}
                        itemPath={itemPath}>
                        <SecurityPolicyDetails/>
                    </SimpleItemDetails>
                </ItemContentTabTwo>
            </TopicContextProvider>
            <AssignModalTwo
                isOpen={showManagedModal}
                onClose={() => setShowManagedModal(false)}
                modalTitle={translate.byKey(TranslationKeys.assignSecurityPolicy)}
                lookup={lookup}
                dataMethod={listParameters => getUserLogins(
                    {
                        ...listParameters,
                        filters: appendFilter(currentItem.siteId, "siteId", "site.id", listParameters?.filters ?? [])
                    },
                    parseInt(user.profileId)
                )}
                tableColumns={UserLoginColumns}
                searchColumns={searchColumns}
                handleSubmit={assignSecurityPolicy}
                getAssignedItems={getAssignedItems}
                assigningTitle={translate.byKey(TranslationKeys.assignSecurityPolicy)}
            />
        </>
    );
};

export default SecurityPolicy;
